import {
  useState,
  useCallback,
  useEffect,
  useContext,
  useLayoutEffect,
} from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import dayjs from 'dayjs';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import {
  useGetSettlementAmountMutation,
  useSaveDraftResignationRequestMutation,
  useSubmitResignationRequestMutation,
} from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';
import {
  resignationRequestCreateRequisitionInfo,
  getReasonOptions,
} from '@Features/EmployeeSelfService/requests/ResignationRequests/store/selectors/resignationRequestSelector';
import { resignationRequestActions } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/resignationRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useHasAccessToSubmit,
  useNavigationToListRequests,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { FooterActions } from '@Enum/index';
import { NotificationContext } from '@Context/NotificationContext';
import { RequestError, ResignationRequestFormRequest } from '@Types/api/form';
import { validateForm } from '@Features/utils/validateForm';

import { initialFormValues } from '../initialValues';
import type {
  ResignationDateChangeHandler,
  ResignationRequestFormValues,
} from '../types';
import { RequestDetailsSection } from '../components';
import { ResignationRequestFormSchema } from '../validationSchema';
import { LAST_WORKING_DATE_DIFF_IN_DAYS } from '../constants';

const ResignationRequestCreate: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(currentUserSelector);
  const requisitionInfo = useAppSelector(
    resignationRequestCreateRequisitionInfo
  );
  const reasonOptions = useAppSelector(getReasonOptions);

  const [disabledSaveForm, setDisableSaveForm] = useState(false);

  const [saveDraftResignationRequest] =
    useSaveDraftResignationRequestMutation();
  const [submitRequest] = useSubmitResignationRequestMutation();
  const [getSettlementAmount] = useGetSettlementAmountMutation();
  const hasAccessToSubmit = useHasAccessToSubmit('Resignation');
  const { handleBackToListRequests } = useNavigationToListRequests();

  useLayoutEffect(() => {
    if (!hasAccessToSubmit) {
      setDisableSaveForm(true);
    }
  }, [hasAccessToSubmit]);

  useEffect(
    () => () => {
      dispatch(resignationRequestActions.setResignationRequest(null));
    },
    []
  );

  const handleSubmitForm = useCallback(
    (values: ResignationRequestFormValues): void => {
      const submitForm = async (): Promise<void> => {
        let body = {
          empNumber: currentUser.empNumber,
          resDate: values.resignationDate,
          lastWrkngDate: values.lastWorkingDate,
          letterContent: values.letterOfContent,
          reason: values.reason as number,
        } as ResignationRequestFormRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const { createdDate, statusId, requestId } =
            await saveDraftResignationRequest(body).unwrap();

          dispatch(
            resignationRequestActions.setResignationRequest({
              createdDate,
              requestId,
              reqStatusId: statusId,
            })
          );

          await submitRequest(String(requestId)).unwrap();

          submitSuccessNotification(String(requestId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitForm();
    },
    [currentUser, requisitionInfo, handleBackToListRequests]
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: ResignationRequestFormSchema,
    onSubmit: handleSubmitForm,
  });

  const onResignationDateChange: ResignationDateChangeHandler = useCallback(
    (value) => {
      if (!value) {
        formik.setFieldValue('lastWorkingDate', '');
        formik.setFieldValue('approxSettlementAmount', '');
        return;
      }

      const lastWorkingDate = dayjs(value).add(
        LAST_WORKING_DATE_DIFF_IN_DAYS,
        'days'
      );

      formik.setFieldValue('lastWorkingDate', lastWorkingDate);

      getSettlementAmount(dayjs(value).toISOString())
        .unwrap()
        .then((response) => {
          formik.setFieldValue('approxSettlementAmount', response);
        })
        .catch(() => {
          formik.setFieldValue('approxSettlementAmount', '');
        });
    },
    [formik]
  );

  const handleSaveAsDraft = useCallback((): void => {
    const body = {
      empNumber: currentUser.empNumber,
      resDate: formik.values.resignationDate,
      lastWrkngDate: formik.values.lastWorkingDate,
      letterContent: formik.values.letterOfContent,
      reason: formik.values.reason as number,
    };

    const saveAsDraft = async (): Promise<void> => {
      setDisableSaveForm(true);

      try {
        const { requestId, createdDate, statusId } =
          await saveDraftResignationRequest(body).unwrap();

        dispatch(
          resignationRequestActions.setResignationRequest({
            createdDate,
            requestId,
            reqStatusId: statusId,
          })
        );

        saveSuccessNotification(String(requestId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(
        ResignationRequestFormSchema,
        formik
      );

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [currentUser, formik, handleBackToListRequests]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        disabledSaveForm={disabledSaveForm}
        formType="create"
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            reasonOptions={reasonOptions}
            onResignationDateChange={onResignationDateChange}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default ResignationRequestCreate;
